import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Row,
  Col,
  Divider,
  Card,
  Space,
  Radio,
  Checkbox,
  InputNumber,
} from "antd";
import {
  DeleteOutlined,
  DragOutlined,
  PlusOutlined,
  EditOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Service from "../../service";
import {
  PREDEFINED_SECTION,
  PREDEFINED_FIELDS,
  SAME_AS_PRESENT_ADDRESS_LABEL,
  PRESENT_ADDRESS_LAST_FIELD,
} from "../../constants/onboardingPredefinedFields";

const { TextArea } = Input;
const { Option } = Select;

const LoadingOverlay = ({ loading, children }) => {
  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {children}
    </div>
  );
};

const OnBoardingFormBuilder = () => {
  // const API_URL = `${import.meta.env.VITE_TM_API_URL}`;
  const [form] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const [predefinedSectionForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allFields, setAllFields] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [isAddingField, setIsAddingField] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [isEditingPredefinedSection, setIsEditingPredefinedSection] = useState(false);
  const [isDefaultEdit, setIsDefaultEdit] = useState(false);
  const [predefinedConfig, setPredefinedConfig] = useState({
    predefinedSectionActive: true,
    predefinedFieldsActive: {},
    predefinedFieldsRequired: {},
    predefinedMinFieldsRequired: 0,
  });
  const [togglingKey, setTogglingKey] = useState(null);

  const fieldTypes = [
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "number", label: "Number" },
    { value: "phone", label: "Phone Number" },
    { value: "url", label: "URL" },
    { value: "textarea", label: "Text Area" },
    { value: "select", label: "Select Dropdown" },
    { value: "multiselect", label: "Multi Select" },
    { value: "radio", label: "Radio Button" },
    { value: "checkbox", label: "Checkbox" },
    { value: "date", label: "Date" },
    { value: "datetime", label: "Date Time" },
    { value: "time", label: "Time" },
    { value: "file", label: "File Upload" },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadSections(), loadFields(), loadPredefinedConfig()]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPredefinedConfig = async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.getPredefinedConfig,
      });
      if (response.data && response.data.data) {
        setPredefinedConfig({
          predefinedSectionActive: response.data.data.predefinedSectionActive !== false,
          predefinedFieldsActive: response.data.data.predefinedFieldsActive || {},
          predefinedFieldsRequired: response.data.data.predefinedFieldsRequired || {},
          predefinedMinFieldsRequired: response.data.data.predefinedMinFieldsRequired || 0,
        });
      }
    } catch (error) {
      console.error("Error loading predefined config:", error);
    }
  };

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${Service.getSections}?includeInactive=true`,
      });
      setAllSections(response.data.data || []);
    } catch (error) {
      console.error("Error loading sections:", error);
      message.error("Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.getFields,
      });
      const dbFields = response.data.data || [];
      setAllFields(dbFields);
    } catch (error) {
      console.error("Error loading fields:", error);
      message.error("Failed to load fields");
    } finally {
      setLoading(false);
    }
  };

  // Section handlers
  const handleAddSection = () => {
    setIsAddingSection(true);
    setIsAddingField(false);
    setEditingSection(null);
    sectionForm.resetFields();
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setIsAddingSection(true);
    setIsAddingField(false);
    sectionForm.setFieldsValue({
      sectionName: section.sectionName,
      sectionKey: section.sectionKey,
      minFieldsRequired: section.minFieldsRequired || 0,
    });
  };

  const handleSaveSection = async (values) => {
    try {
      setLoading(true);

      if (editingSection) {
        // await axios.put(`${API_URL}/api/company-sections/${editingSection._id}`, values);
        await Service.makeAPICall({
          methodName: Service.putMethod,
          api_url: `${Service.editSection}/${editingSection._id}`,
          body: values,
        });
        message.success("Section updated successfully");
        await loadData();
      } else {
        // await axios.post(`${API_URL}/api/company-sections`, values);
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.createSection,
          body: values,
        });
        console.log(response.data);
        message.success("Section added successfully");
        await loadSections();
      }

      setIsAddingSection(false);
      setEditingSection(null);
      sectionForm.resetFields();
    } catch (error) {
      console.error("Save section failed:", error);
      message.error(error.response?.data?.message || "Failed to save section");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId, isDefault) => {
    if (isDefault) {
      message.warning(
        "Cannot delete default sections. You can only edit their names."
      );
      return;
    }

    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.deleteMethod,
        api_url: Service.deleteSection + `/${sectionId}`,
      });

      if (
        response &&
        (response.status === 200 ||
          response.status === 201 ||
          response.status === 204)
      ) {
        message.success("Section deleted successfully");
        await Promise.all([loadSections(), loadFields()]);
      } else {
        message.error(response?.data?.message || "Failed to delete section");
      }
    } catch (error) {
      console.error("Delete section failed:", error);
      message.error(
        error.response?.data?.message || "Failed to delete section"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSectionDragEnd = async (result) => {
    if (!result.destination) return;

    const newSections = [...allSections];
    const [movedSection] = newSections.splice(result.source.index, 1);
    newSections.splice(result.destination.index, 0, movedSection);

    const reorderedSections = newSections.map((section, index) => ({
      ...section,
      order: index,
    }));

    setAllSections(reorderedSections);

    try {
      const sectionsToUpdate = reorderedSections.map((s) => ({
        _id: s._id,
        order: s.order,
      }));

      // await axios.put(`${API_URL}/api/company-sections/reorder/all`, {
      //   sections: sectionsToUpdate
      // });
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.reorderSection,
        body: { sections: sectionsToUpdate },
      });
      message.success("Sections reordered successfully");
    } catch (error) {
      console.error("Section reorder failed:", error);
      message.error("Failed to save section order");
      loadSections();
    }
  };

  // Field handlers
  const handleAddField = () => {
    setIsAddingField(true);
    setIsAddingSection(false);
    setEditingField(null);
    form.resetFields();
    form.setFieldsValue({ required: false, sectionKey: allSections[0]?.sectionKey });
  };

  const closeSectionModal = () => {
    setIsAddingSection(false);
    setEditingSection(null);
    sectionForm.resetFields();
  };

  const closeFieldModal = () => {
    setIsAddingField(false);
    setEditingField(null);
    form.resetFields();
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setIsAddingField(true);
    setIsAddingSection(false);
    setIsDefaultEdit(field.isDefault);
    form.setFieldsValue({
      fieldLabel: field.fieldLabel,
      fieldName: field.fieldName,
      sectionKey: field.sectionKey,
      fieldType: field.fieldType,
      placeholder: field.placeholder || "",
      options: field.options ? field.options : [],
      required: field.required || false,
    });
  };

  const handleSaveField = async (values) => {
    try {
      setLoading(true);

      const selectedSection = allSections.find(
        (s) => s.sectionKey === values.sectionKey
      );

      const fieldData = {
        fieldName: values.fieldName,
        fieldLabel: values.fieldLabel,
        fieldType: values.fieldType,
        section: selectedSection?.sectionName || "",
        sectionKey: values.sectionKey,
        placeholder: values.placeholder || "",
        options: values.options ? values.options : [],
        required: values.required || false,
      };

      if (editingField) {
        // await axios.put(`${API_URL}/api/company-fields/${editingField._id}`, fieldData);
        await Service.makeAPICall({
          methodName: Service.putMethod,
          api_url: `${Service.editField}/${editingField._id}`,
          body: fieldData,
        });
        message.success("Field updated successfully");
      } else {
        fieldData.order = allFields.filter(
          (f) => f.sectionKey === values.sectionKey
        ).length;
        // await axios.post(`${API_URL}/api/company-fields`, fieldData);
        await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.createtField,
          body: fieldData,
        });
        message.success("Field added successfully");
      }

      await loadFields();
      setIsAddingField(false);
      setEditingField(null);
      form.resetFields();
    } catch (error) {
      console.error("Save failed:", error);
      message.error(error.response?.data?.message || "Failed to save field");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (fieldId, isDefault) => {
    if (isDefault) {
      message.warning("Cannot delete default fields");
      return;
    }

    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.deleteMethod,
        api_url: `${Service.deleteField}/${fieldId}`,
      });

      if (response && (response.status === 200 || response.status === 201 || response.status === 204)) {
        message.success("Field deleted successfully");
        await loadFields();
      } else {
        message.error(response?.data?.message || "Failed to delete field");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      message.error(error.response?.data?.message || "Failed to delete field");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceSection = result.source.droppableId;
    const destSection = result.destination.droppableId;

    if (sourceSection !== destSection) {
      message.info("Cannot move fields between sections");
      return;
    }

    const newAllFields = [...allFields];
    const sectionFields = newAllFields.filter(
      (f) => f.sectionKey === sourceSection
    );
    const otherFields = newAllFields.filter(
      (f) => f.sectionKey !== sourceSection
    );

    const [movedField] = sectionFields.splice(result.source.index, 1);
    sectionFields.splice(result.destination.index, 0, movedField);

    const reorderedSectionFields = sectionFields.map((field, index) => ({
      ...field,
      order: index,
    }));

    const finalFields = [...otherFields, ...reorderedSectionFields];
    setAllFields(finalFields);

    try {
      const fieldsToUpdate = finalFields.map((f) => ({
        _id: f._id,
        order: f.order,
        section: f.section,
        sectionKey: f.sectionKey,
      }));

      // await axios.put(`${API_URL}/api/company-fields/reorder`, {
      //   fields: fieldsToUpdate
      // });
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.reorderField,
        body: { fields: fieldsToUpdate },
      });

      message.success("Fields reordered successfully");
    } catch (error) {
      console.error("Reorder failed:", error);
      message.error("Failed to save field order");
      loadFields();
    }
  };

  const handleSectionActiveToggle = async (section, checked) => {
    const key = `section-${section._id}`;
    setTogglingKey(key);
    const hideLoading = message.loading("Updating...", 0);
    try {
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: `${Service.editSection}/${section._id}`,
        body: { isActive: checked },
      });
      message.success(checked ? "Section enabled for form" : "Section disabled for form");
      setAllSections((prev) =>
        prev.map((s) => (s._id === section._id ? { ...s, isActive: checked } : s))
      );
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update section");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  const handleFieldActiveToggle = async (field, checked) => {
    const key = `field-${field._id}`;
    setTogglingKey(key);
    const hideLoading = message.loading("Updating...", 0);
    try {
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: `${Service.editField}/${field._id}`,
        body: { isActive: checked },
      });
      message.success(checked ? "Field enabled for form" : "Field disabled for form");
      setAllFields((prev) =>
        prev.map((f) => (f._id === field._id ? { ...f, isActive: checked } : f))
      );
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update field");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  const handlePredefinedSectionToggle = async (checked) => {
    setTogglingKey("predefined-section");
    const hideLoading = message.loading("Updating...", 0);
    try {
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.updatePredefinedConfig,
        body: { predefinedSectionActive: checked },
      });
      message.success(checked ? "Predefined section enabled for form" : "Predefined section disabled for form");
      setPredefinedConfig((prev) => ({ ...prev, predefinedSectionActive: checked }));
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update predefined section");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  const handlePredefinedFieldToggle = async (fieldName, checked) => {
    const key = `predefined-field-${fieldName}`;
    setTogglingKey(key);
    const hideLoading = message.loading("Updating...", 0);
    try {
      const nextFieldsActive = { ...predefinedConfig.predefinedFieldsActive, [fieldName]: checked };
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.updatePredefinedConfig,
        body: { predefinedFieldsActive: nextFieldsActive },
      });
      message.success(checked ? "Predefined field enabled for form" : "Predefined field disabled for form");
      setPredefinedConfig((prev) => ({
        ...prev,
        predefinedFieldsActive: nextFieldsActive,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update predefined field");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  // Predefined field mandatory/optional override
  const handlePredefinedFieldRequiredChange = async (fieldName, required) => {
    const key = `predefined-required-${fieldName}`;
    setTogglingKey(key);
    const hideLoading = message.loading("Updating...", 0);
    try {
      const nextFieldsRequired = { ...predefinedConfig.predefinedFieldsRequired, [fieldName]: required };
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.updatePredefinedConfig,
        body: { predefinedFieldsRequired: nextFieldsRequired },
      });
      message.success(required ? "Field set to Mandatory" : "Field set to Optional");
      setPredefinedConfig((prev) => ({
        ...prev,
        predefinedFieldsRequired: nextFieldsRequired,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update predefined field");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  // Predefined section minimum-fields-to-fill rule (edited via its "Edit Section" modal)
  const handleOpenPredefinedSectionEdit = () => {
    predefinedSectionForm.setFieldsValue({
      minFieldsRequired: predefinedConfig.predefinedMinFieldsRequired || 0,
    });
    setIsEditingPredefinedSection(true);
  };

  const closePredefinedSectionModal = () => {
    setIsEditingPredefinedSection(false);
    predefinedSectionForm.resetFields();
  };

  const handleSavePredefinedSection = async (values) => {
    try {
      setLoading(true);
      const minFieldsRequired = values.minFieldsRequired || 0;
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: Service.updatePredefinedConfig,
        body: { predefinedMinFieldsRequired: minFieldsRequired },
      });
      message.success("Section updated successfully");
      setPredefinedConfig((prev) => ({
        ...prev,
        predefinedMinFieldsRequired: minFieldsRequired,
      }));
      closePredefinedSectionModal();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update section");
    } finally {
      setLoading(false);
    }
  };

  const isPredefinedFieldActive = (field) =>
    predefinedConfig.predefinedFieldsActive[field.fieldName] !== false;

  const isPredefinedFieldRequired = (field) => {
    const override = predefinedConfig.predefinedFieldsRequired?.[field.fieldName];
    return override === undefined ? field.required : override;
  };

  // Mandatory/Optional toggle for an admin-built custom field (updates its `required`).
  const handleCustomFieldRequiredChange = async (field, required) => {
    const key = `field-required-${field._id}`;
    setTogglingKey(key);
    const hideLoading = message.loading("Updating...", 0);
    try {
      await Service.makeAPICall({
        methodName: Service.putMethod,
        api_url: `${Service.editField}/${field._id}`,
        body: { required },
      });
      message.success(required ? "Field set to Mandatory" : "Field set to Optional");
      setAllFields((prev) =>
        prev.map((f) => (f._id === field._id ? { ...f, required } : f))
      );
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update field");
    } finally {
      hideLoading();
      setTogglingKey(null);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      disabled: true,
      style: { width: "100%" },
    };

    switch (field.fieldType) {
      case "text":
        return <Input {...commonProps} />;

      case "email":
        return <Input {...commonProps} type="email" />;

      case "number":
        return <Input {...commonProps} type="number" />;

      case "phone":
        return <Input {...commonProps} type="tel" />;

      case "url":
        return <Input {...commonProps} type="url" />;

      case "textarea":
        return <TextArea {...commonProps} rows={1} />;

      case "select":
        return (
          <Select {...commonProps} showSearch>
            {(field.options || []).map((opt, idx) => (
              <Select.Option key={opt.value || idx} value={opt.value || opt}>
                {opt.key || opt}
              </Select.Option>
            ))}
          </Select>
        );

      case "multiselect":
        return (
          <Select {...commonProps} mode="multiple" showSearch>
            {(field.options || []).map((opt, idx) => (
              <Select.Option key={opt.value || idx} value={opt.value || opt}>
                {opt.key || opt}
              </Select.Option>
            ))}
          </Select>
        );

      case "radio":
        return (
          <Radio.Group disabled>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {(field.options || []).map((opt, idx) => (
                <Radio key={idx} value={opt.value || opt}>
                  {opt.key || opt}
                </Radio>
              ))}
            </div>
          </Radio.Group>
        );

      case "checkbox":
        return (
          <Checkbox.Group disabled>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {(field.options || []).map((opt, idx) => (
                <Checkbox key={idx} value={opt.value || opt}>
                  {opt.key || opt}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        );

      case "date":
        return <Input {...commonProps} type="date" />;

      case "datetime":
        return <Input {...commonProps} type="datetime-local" />;

      case "time":
        return <Input {...commonProps} type="time" />;

      case "file":
        return (
          <Input
            type="file"
            disabled
            style={{ width: "100%" }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
        );

      default:
        return <Input {...commonProps} />;
    }
  };

  const needsOptions = ["select", "multiselect", "radio", "checkbox"];
  const currentFieldType = Form.useWatch("fieldType", form);

  const groupedFields = allSections.map((section) => ({
    ...section,
    fields: allFields
      .filter((f) => f.sectionKey === section.sectionKey)
      .sort((a, b) => a.order - b.order),
  }));

  const predefinedFieldsSorted = [...PREDEFINED_FIELDS].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  return (
    <Card className="employee-card addleave-btn">
      <div className=" inout-header text-left">
        <h2>On-boarding Form Builder</h2>
        <div className="header-btns">
          <Button
            type="primary"
            onClick={handleAddSection}
            className="add-btn"
            icon={<PlusOutlined />}
          >
            Add Section
          </Button>
          <Button
            type="primary"
            onClick={handleAddField}
            icon={<PlusOutlined />}
          >
            Add Custom Field
          </Button>
        </div>
      </div>

      <Card className="main-content-wrapper">
        <LoadingOverlay loading={loading}>
          <div style={{ padding: "24px 0", width: "100%" }}>
            <Row gutter={24}>
              <Col span={24}>
                <div style={{ minHeight: "calc(100vh - 200px)", width: "100%" }}>
                  <div style={{ width: "100%" }}>
                    {/* Predefined section: read-only, not editable or movable */}
                    <div
                      style={{
                        marginBottom: 32,
                        padding: "0 0 24px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 12,
                        }}
                      >
                        <h3
                          style={{
                            color: "#03497a",
                            fontSize: 18,
                            fontWeight: 600,
                            margin: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {PREDEFINED_SECTION.sectionName}
                          <span
                            style={{
                              fontSize: 11,
                              color: "#666",
                              background: "#e6f7ff",
                              padding: "2px 8px",
                              borderRadius: 3,
                              fontWeight: 400,
                            }}
                          >
                            Predefined (read-only)
                          </span>
                          {/* <Switch
                          size="small"
                          checked={predefinedConfig.predefinedSectionActive}
                          onChange={handlePredefinedSectionToggle}
                          loading={togglingKey === "predefined-section"}
                          disabled={togglingKey === "predefined-section"}
                          title={predefinedConfig.predefinedSectionActive ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
                        /> */}
                        </h3>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          {(predefinedConfig.predefinedMinFieldsRequired || 0) > 0 && (
                            <span style={{ fontSize: 12, color: "#666" }}>
                              Minimum fields required to fill: {predefinedConfig.predefinedMinFieldsRequired} of{" "}
                              {predefinedFieldsSorted.filter((f) => isPredefinedFieldActive(f)).length}
                            </span>
                          )}
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={handleOpenPredefinedSectionEdit}
                          />
                        </div>
                      </div>
                      <Divider style={{ margin: "12px 0 24px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "24px",
                          width: "100%",
                        }}
                      >
                        {predefinedFieldsSorted.map((field) => (
                          <React.Fragment key={field.fieldName}>
                            <div
                              style={{
                                width: "calc(33.333% - 16px)",
                                minWidth: "280px",
                                flex: "0 0 auto",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: 6,
                                  gap: 8,
                                  minWidth: 0,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 500,
                                    fontSize: 14,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {field.fieldLabel}
                                  {isPredefinedFieldRequired(field) && (
                                    <span style={{ color: "red", marginLeft: 4 }}>
                                      *
                                    </span>
                                  )}
                                </div>
                                <Switch
                                  size="small"
                                  checked={isPredefinedFieldActive(field)}
                                  onChange={(checked) =>
                                    handlePredefinedFieldToggle(field.fieldName, checked)
                                  }
                                  loading={togglingKey === `predefined-field-${field.fieldName}`}
                                  disabled={togglingKey === `predefined-field-${field.fieldName}`}
                                  title={isPredefinedFieldActive(field) ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
                                  style={{ flexShrink: 0 }}
                                />
                              </div>
                              {renderField(field)}
                              <Radio.Group
                                size="small"
                                value={isPredefinedFieldRequired(field)}
                                onChange={(e) =>
                                  handlePredefinedFieldRequiredChange(field.fieldName, e.target.value)
                                }
                                disabled={
                                  !isPredefinedFieldActive(field) ||
                                  togglingKey === `predefined-required-${field.fieldName}`
                                }
                                style={{ marginTop: "auto", paddingTop: 8 }}
                              >
                                <Radio value={true}>Mandatory</Radio>
                                <Radio value={false}>Optional</Radio>
                              </Radio.Group>
                            </div>
                            {field.fieldName === PRESENT_ADDRESS_LAST_FIELD && (
                              <div style={{ width: "100%", flex: "0 0 100%" }}>
                                <Checkbox disabled>{SAME_AS_PRESENT_ADDRESS_LABEL}</Checkbox>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    <DragDropContext onDragEnd={handleSectionDragEnd}>
                      <Droppable droppableId="sections">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {allSections.map((section, sectionIndex) => (
                              <Draggable
                                key={section._id}
                                draggableId={section._id}
                                index={sectionIndex}
                              >
                                {(sectionProvided, sectionSnapshot) => (
                                  <div
                                    ref={sectionProvided.innerRef}
                                    {...sectionProvided.draggableProps}
                                    style={{
                                      ...sectionProvided.draggableProps.style,
                                      marginBottom: 32,
                                      padding: sectionSnapshot.isDragging
                                        ? 16
                                        : 0,
                                      background: sectionSnapshot.isDragging
                                        ? "#f5f7fa"
                                        : "transparent",
                                      borderRadius: 8,
                                      border: sectionSnapshot.isDragging
                                        ? "2px dashed #03497a"
                                        : "none",
                                    }}
                                  >
                                    <div className="form-section">
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          marginBottom: 12,
                                        }}
                                      >
                                        <h3
                                          style={{
                                            color: "#03497a",
                                            fontSize: 18,
                                            fontWeight: 600,
                                            margin: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <span
                                            {...sectionProvided.dragHandleProps}
                                            style={{
                                              cursor: "grab",
                                              display: "inline-flex",
                                              padding: 4,
                                            }}
                                          >
                                            <DragOutlined
                                              style={{
                                                color: sectionSnapshot.isDragging
                                                  ? "#03497a"
                                                  : "#999",
                                              }}
                                            />
                                          </span>
                                          {section.sectionName}
                                          {section.isDefault && (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                color: "#999",
                                                background: "#f0f0f0",
                                                padding: "2px 8px",
                                                borderRadius: 3,
                                                fontWeight: 400,
                                              }}
                                            >
                                              Default
                                            </span>
                                          )}
                                          <Switch
                                            size="small"
                                            checked={section.isActive !== false}
                                            onChange={(checked) =>
                                              handleSectionActiveToggle(section, checked)
                                            }
                                            loading={togglingKey === `section-${section._id}`}
                                            disabled={togglingKey === `section-${section._id}`}
                                            title={section.isActive !== false ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
                                          />
                                        </h3>
                                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                          {(section.minFieldsRequired || 0) > 0 && (
                                            <span style={{ fontSize: 12, color: "#666" }}>
                                              Minimum fields required to fill: {section.minFieldsRequired} of{" "}
                                              {groupedFields.find((g) => g.sectionKey === section.sectionKey)
                                                ?.fields.filter((f) => f.isActive !== false).length || 0}
                                            </span>
                                          )}
                                          <div style={{ display: "flex", gap: 8 }}>
                                          <Button
                                            type="text"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() =>
                                              handleEditSection(section)
                                            }
                                          />
                                          {!section.isDefault && (
                                            <Popconfirm
                                              placement="topLeft"
                                              title={
                                                <span>
                                                  Are you sure you want to delete the section "
                                                  {section.sectionName}" <br /> and all fields related to it?
                                                </span>
                                              }
                                              okText="Yes"
                                              cancelText="No"
                                              okButtonProps={{ danger: true }}
                                              zIndex={1100}
                                              getPopupContainer={() => document.body}
                                              onConfirm={() => handleDeleteSection(section._id, section.isDefault)}
                                            >
                                              <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                          )}
                                          </div>
                                        </div>
                                      </div>
                                      <Divider
                                        style={{ margin: "12px 0 24px 0" }}
                                      />

                                      <DragDropContext
                                        onDragEnd={handleFieldDragEnd}
                                      >
                                        <Droppable
                                          droppableId={section.sectionKey}
                                        >
                                          {(provided) => (
                                            <div
                                              {...provided.droppableProps}
                                              ref={provided.innerRef}
                                              style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "24px",
                                                width: "100%",
                                                minHeight:
                                                  groupedFields.find(
                                                    (g) =>
                                                      g.sectionKey ===
                                                      section.sectionKey
                                                  )?.fields.length === 0
                                                    ? 80
                                                    : "auto",
                                              }}
                                            >
                                              {groupedFields.find(
                                                (g) =>
                                                  g.sectionKey ===
                                                  section.sectionKey
                                              )?.fields.length === 0 ? (
                                                <div
                                                  style={{
                                                    width: "100%",
                                                    textAlign: "center",
                                                    padding: "24px",
                                                    color: "#999",
                                                    background: "#fafafa",
                                                    borderRadius: 6,
                                                    border: "1px dashed #d9d9d9",
                                                  }}
                                                >
                                                  No fields in this section. Add a
                                                  field to get started.
                                                </div>
                                              ) : (
                                                groupedFields
                                                  .find(
                                                    (g) =>
                                                      g.sectionKey ===
                                                      section.sectionKey
                                                  )
                                                  ?.fields.map((field, index) => (
                                                    <Draggable
                                                      key={field._id}
                                                      draggableId={field._id}
                                                      index={index}
                                                    >
                                                      {(provided, snapshot) => (
                                                        <div
                                                          ref={provided.innerRef}
                                                          {...provided.draggableProps}
                                                          style={{
                                                            ...provided
                                                              .draggableProps
                                                              .style,
                                                            width:
                                                              "calc(33.333% - 16px)",
                                                            minWidth: "280px",
                                                            flex: "0 0 auto",
                                                            // Column card, so the
                                                            // Mandatory/Optional row
                                                            // below can be pinned to
                                                            // the bottom and line up
                                                            // across the row.
                                                            display: "flex",
                                                            flexDirection: "column",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding:
                                                                snapshot.isDragging
                                                                  ? 12
                                                                  : 0,
                                                              background:
                                                                snapshot.isDragging
                                                                  ? "#f5f7fa"
                                                                  : "transparent",
                                                              borderRadius: 6,
                                                              flex: 1,
                                                              display: "flex",
                                                              flexDirection: "column",
                                                              border:
                                                                snapshot.isDragging
                                                                  ? "2px dashed #03497a"
                                                                  : "none",
                                                              boxShadow:
                                                                snapshot.isDragging
                                                                  ? "0 4px 12px rgba(0,0,0,0.15)"
                                                                  : "none",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                display: "flex",
                                                                alignItems:
                                                                  "center",
                                                                justifyContent:
                                                                  "space-between",
                                                                marginBottom: 6,
                                                              }}
                                                            >
                                                              <label
                                                                style={{
                                                                  fontWeight: 500,
                                                                  display: "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  flex: 1,
                                                                  fontSize: 14,
                                                                }}
                                                              >
                                                                <span
                                                                  {...provided.dragHandleProps}
                                                                  style={{
                                                                    cursor:
                                                                      "grab",
                                                                    marginRight: 8,
                                                                    display:
                                                                      "inline-flex",
                                                                    alignItems:
                                                                      "center",
                                                                    padding: 4,
                                                                    borderRadius: 4,
                                                                    background:
                                                                      snapshot.isDragging
                                                                        ? "#03497a"
                                                                        : "transparent",
                                                                  }}
                                                                >
                                                                  <DragOutlined
                                                                    style={{
                                                                      color:
                                                                        snapshot.isDragging
                                                                          ? "white"
                                                                          : "#999",
                                                                      fontSize: 14,
                                                                    }}
                                                                  />
                                                                </span>
                                                                {field.fieldLabel}
                                                                {field.required && (
                                                                  <span
                                                                    style={{
                                                                      color:
                                                                        "red",
                                                                      marginLeft: 4,
                                                                    }}
                                                                  >
                                                                    *
                                                                  </span>
                                                                )}
                                                                {field.isDefault && (
                                                                  <span
                                                                    style={{
                                                                      fontSize: 11,
                                                                      color:
                                                                        "#999",
                                                                      marginLeft: 6,
                                                                      background:
                                                                        "#f0f0f0",
                                                                      padding:
                                                                        "2px 6px",
                                                                      borderRadius: 3,
                                                                    }}
                                                                  >
                                                                    Default
                                                                  </span>
                                                                )}
                                                              </label>
                                                              <div
                                                                style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                                  gap: 12,
                                                                }}
                                                              >
                                                                <Switch
                                                                  size="small"
                                                                  checked={field.isActive !== false}
                                                                  onChange={(checked) =>
                                                                    handleFieldActiveToggle(field, checked)
                                                                  }
                                                                  loading={togglingKey === `field-${field._id}`}
                                                                  disabled={togglingKey === `field-${field._id}`}
                                                                  title={field.isActive !== false ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
                                                                />
                                                                <Button
                                                                  type="text"
                                                                  size="small"
                                                                  icon={<EditOutlined />}
                                                                  title="Edit field"
                                                                  onClick={() =>
                                                                    handleEditField(
                                                                      field
                                                                    )
                                                                  }
                                                                />
                                                                {!field.isDefault && (
                                                                  <Popconfirm
                                                                    title="Are you sure you want to delete this field?"
                                                                    okText="Yes"
                                                                    cancelText="No"
                                                                    okButtonProps={{ danger: true }}
                                                                    zIndex={1100}
                                                                    getPopupContainer={() => document.body}
                                                                    onConfirm={() => handleDeleteField(field._id, field.isDefault)}
                                                                  >
                                                                    <Button
                                                                      type="text"
                                                                      danger
                                                                      size="small"
                                                                      icon={<DeleteOutlined />}
                                                                      title="Delete field"
                                                                    />
                                                                  </Popconfirm>
                                                                )}
                                                              </div>
                                                            </div>
                                                            {renderField(field)}
                                                            <Radio.Group
                                                              size="small"
                                                              value={field.required}
                                                              onChange={(e) =>
                                                                handleCustomFieldRequiredChange(
                                                                  field,
                                                                  e.target.value
                                                                )
                                                              }
                                                              disabled={
                                                                field.isActive === false ||
                                                                togglingKey ===
                                                                `field-required-${field._id}`
                                                              }
                                                              style={{ marginTop: "auto", paddingTop: 8 }}
                                                            >
                                                              <Radio value={true}>
                                                                Mandatory
                                                              </Radio>
                                                              <Radio value={false}>
                                                                Optional
                                                              </Radio>
                                                            </Radio.Group>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </Draggable>
                                                  ))
                                              )}
                                              {provided.placeholder}
                                            </div>
                                          )}
                                        </Droppable>
                                      </DragDropContext>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </LoadingOverlay>
      </Card>

      <Modal
        title={editingSection ? "Edit Section" : "Add New Section"}
        open={isAddingSection}
        onCancel={closeSectionModal}
        footer={[
          <Button key="cancel" onClick={closeSectionModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => sectionForm.submit()}
          >
            {editingSection ? "Update Section" : "Add Section"}
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={sectionForm}
          layout="vertical"
          onFinish={handleSaveSection}
        >
          <Form.Item
            name="sectionName"
            label="Section Name"
            rules={[{ required: true, message: "Please enter section name!" }]}
          >
            <Input
              placeholder="e.g., Basic Information"
              onChange={(e) => {
                const name = e.target.value;
                if (!editingSection) {
                  const key = name
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9\s]/g, "")
                    .replace(/\s+/g, "_");
                  sectionForm.setFieldsValue({ sectionKey: key });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="sectionKey"
            label="Section Key"
            rules={[
              { required: true, message: "Please enter section key!" },
              {
                pattern: /^[a-z_][a-z0-9_]*$/,
                message:
                  "Only lowercase letters, numbers, and underscores allowed",
              },
            ]}
          >
            <Input placeholder="e.g., company_details" disabled />
          </Form.Item>
          {editingSection && (
            <Form.Item
              name="minFieldsRequired"
              label="Minimum Fields Required to Fill"
              initialValue={0}
              extra="Out of this section's fields, the minimum number the user must fill in: any fields, not specific ones."
            >
              <InputNumber
                min={0}
                max={
                  groupedFields.find((g) => g.sectionKey === editingSection.sectionKey)
                    ?.fields.filter((f) => f.isActive !== false).length || 0
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Edit Section"
        open={isEditingPredefinedSection}
        onCancel={closePredefinedSectionModal}
        footer={[
          <Button key="cancel" onClick={closePredefinedSectionModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => predefinedSectionForm.submit()}
          >
            Update Section
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={predefinedSectionForm}
          layout="vertical"
          onFinish={handleSavePredefinedSection}
        >
          <Form.Item label="Section Name">
            <Input value={PREDEFINED_SECTION.sectionName} disabled />
          </Form.Item>
          <Form.Item
            name="minFieldsRequired"
            label="Minimum Fields Required to Fill"
            initialValue={0}
            extra="Out of this section's fields, the minimum number the user must fill in: any fields, not specific ones."
          >
            <InputNumber
              min={0}
              max={predefinedFieldsSorted.filter((f) => isPredefinedFieldActive(f)).length}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingField ? "Edit Custom Field" : "Add Custom Field"}
        open={isAddingField}
        onCancel={closeFieldModal}
        width={520}
        footer={[
          <Button key="cancel" onClick={closeFieldModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            {editingField ? "Update Field" : "Add Field"}
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveField}
          initialValues={{
            required: false,
            sectionKey: allSections[0]?.sectionKey,
          }}
        >
          <Form.Item
            name="fieldLabel"
            label="Field Label"
            rules={[{ required: true, message: "Please enter field label!" }]}
          >
            <Input
              placeholder="e.g., Full Name"
              onChange={(e) => {
                const name = e.target.value;
                if (!editingField) {
                  const key = name
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9\s]/g, "")
                    .replace(/\s+/g, "_");
                  form.setFieldsValue({ fieldName: key });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="fieldName"
            label="Field Name"
            rules={[
              { required: true, message: "Please enter field name!" },
              {
                pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                message:
                  "Only letters, numbers, and underscores allowed",
              },
            ]}
          >
            <Input placeholder="e.g., full_name" disabled />
          </Form.Item>
          <Form.Item
            name="sectionKey"
            label="Section"
            rules={[{ required: true, message: "Please select section!" }]}
          >
            <Select
              placeholder="Select section"
              disabled={isDefaultEdit}
            >
              {allSections.map((section) => (
                <Option
                  key={section.sectionKey}
                  value={section.sectionKey}
                >
                  {section.sectionName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="fieldType"
            label="Field Type"
            rules={[{ required: true, message: "Please select field type!" }]}
          >
            <Select placeholder="Select field type">
              {fieldTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="placeholder" label="Placeholder Text">
            <Input placeholder="Enter placeholder text" />
          </Form.Item>
          {currentFieldType &&
            needsOptions.includes(currentFieldType) && (
              <Form.Item
                name="options"
                rules={[{ required: true, message: "Please enter options!" }]}
              >
                <Form.List name="options">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} align="baseline">
                          <Form.Item
                            {...restField}
                            name={[name, "key"]}
                            rules={[{ required: true, message: "Key required" }]}
                          >
                            <Input
                              placeholder="Option label"
                              onChange={(e) => {
                                const keyName = e.target.value;
                                if (!editingField) {
                                  const generatedValue = keyName
                                    .toLowerCase()
                                    .trim()
                                    .replace(/[^a-z0-9\s]/g, "")
                                    .replace(/\s+/g, "_");
                                  const currentOptions =
                                    form.getFieldValue("options") || [];
                                  currentOptions[name] = {
                                    ...currentOptions[name],
                                    key: keyName,
                                    value: generatedValue,
                                  };
                                  form.setFieldsValue({
                                    options: currentOptions,
                                  });
                                  // setFieldsValue does not run validation, so the
                                  // auto-filled value field would keep showing its
                                  // "Value required" error until submit. Re-validate
                                  // just that cell so the error clears once it is
                                  // filled - and comes back if the key is emptied.
                                  form
                                    .validateFields([["options", name, "value"]])
                                    .catch(() => {});
                                }
                              }}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "value"]}
                            rules={[{ required: true, message: "Value required" }]}
                          >
                            <Input placeholder="value" disabled />
                          </Form.Item>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          Add Option
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Form.Item>
            )}
          <Form.Item
            name="required"
            label="Field Requirement"
          >
            <Radio.Group>
              <Radio value={true}>Mandatory</Radio>
              <Radio value={false}>Optional</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OnBoardingFormBuilder;
